import React, { memo, Fragment, useState, useEffect, useCallback } from 'react';

// react-bootstrap
import { Row, Col, Form, Button, Modal } from 'react-bootstrap';
import { message, TreeSelect, Select, Progress, Input, DatePicker } from 'antd';
import { EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';
// components
import Card from '../../../components/bootstrap/card';
import dayjs from 'dayjs';
import { getCipPagesService } from '../../../services/UserService';

// router
import { Link } from 'react-router-dom';
import { AddAdminUserService, getAllGroupsService, createGroupService, userAccount, searchAdminByIecService, claimAdminToGroupService, createSsoConfigService, checkSsoService } from '../../../services/UserService';

import isEmpty from 'lodash/isEmpty'
import axios from 'axios';
import { SERVER_URL } from '../../../const';

const AdminAdd = memo(() => {
   const { SHOW_PARENT } = TreeSelect;
   const [addUserData, setUserData] = useState({});
   const [formData, setFormData] = useState({
      firstName: '',
      lastName: '',
      iec_no: '',
      mobile: '',
      email: '',
      password: '',
      cc: [],
      bcc: [],
      alternateEmail: [],
      multi_factor_check: false,
      data_visible_from_dt: null,
      data_visible_to_dt: null,
      group_head: 0
   });
   const [userType, setUserType] = useState('admin');
   const [isFormValid, setIsFormValid] = useState(false);
   const [messageApi, contextHolder] = message.useMessage();
   const [isUserCreated, setIsUserCreated] = useState(false);
   const [reportAccessOptions, setCipPages] = useState([]);
   const [uploadAccessOptions, setCipUploadPages] = useState([]);
   const [templateAccessOptions, setCipTemplatePages] = useState([]);
   const [masterUploadAccessOptions, setCipMasterUploadPages] = useState([]);
   const [selectedGroup, setSelectedGroup] = useState(undefined);
   const [groupOptions, setGroupOptions] = useState([]);
   const [searchText, setSearchText] = useState('')
   const [showAddGroupModal, setShowAddGroupModal] = useState(false)
   const [newGroupName, setNewGroupName] = useState('')
   const isGroupNotFound = searchText.trim() !== '' && !groupOptions.some(
      (opt) => (opt.label || '').toString().trim().toLowerCase() === searchText.trim().toLowerCase()
   );

   const [selectedReportAccess, setSelectedReportAccess] = useState([]);
   const [selectedUploadAccess, setSelectedUploadAccess] = useState([]);
   const [selectedTemplateAccess, setSelectedTemplateAccess] = useState([]);
   const [selectedDocumentAccess, setSelectedDocumentAccess] = useState([0]); // 0 = 'No', 1 = 'Yes'
   const [selectedMasterUploadAccess, setSelectedMasterUploadAccess] = useState([]);
   const [selectedGraphsAccess, setSelectedGraphsAccess] = useState([0]); // 0 = 'No', 1 = 'Yes'
   const [graphsPageId, setGraphsPageId] = useState(null);
   const [documentPageId, setDocumentPageId] = useState(null);
   const [linkType, setLinkType] = useState('');
   const [linkURL, setLinkURL] = useState('');
   const [documentLinks, setDocumentLinks] = useState([]);
   const [strength, setStrength] = useState(0);
   const [passwordError, setPasswordError] = useState("");
   const [existingAdmin, setExistingAdmin] = useState(null);
   const [searchingAdmin, setSearchingAdmin] = useState(false);

   const [enableSSO, setEnableSSO] = useState(
      localStorage.getItem('role') === 'group_head' || localStorage.getItem('role') === 'admin'
   );
   const [ssoData, setSsoData] = useState({
      domain_name: '',
      tenant_id: '',
      client_id: '',
      client_secret: '',
      sso_provider: 'azure',
      is_active: true
   });

   const DocumentAccessOptions = [
      { label: 'No', value: 0, key: '0' },
      { label: 'Yes', value: 1, key: '1' }
   ];
   const GraphsAccessOptions = [
      { label: 'No', value: 0, key: '0' },
      { label: 'Yes', value: 1, key: '1' }
   ];
   const linkTypeOptions = [
      { label: 'D2D Exp. Docs', value: 'D2D Exp. Docs' },
      { label: 'D2D Imp. Docs', value: 'D2D Imp. Docs' },
      { label: 'D2D Courier BE. Docs', value: 'D2D Courier BE. Docs' },
      { label: 'e-BRC', value: 'e-BRC' },
      { label: 'Other Docs', value: 'Other Docs' }
   ]

   const [userDetails, setUserDetails] = useState({
      role: localStorage.getItem('role'),
      iec: localStorage.getItem('iec_no'),
      name: localStorage.getItem('name'),
      group_id: localStorage.getItem('group_id')
   });
   const checkFormValid = useCallback(() => {
      const { firstName, lastName, iec_no, mobile, email, password } = formData;
      const isPasswordValid = enableSSO ? true : (!password || !passwordError);

      // IEC is required for superadmin adding an admin, or group_head adding any user.
      // But NOT required when superadmin is adding a group_head (that field is hidden).
      const iecRequired = (userDetails.role === 'group_head') ||
         (userDetails.role === 'superadmin' && (userType === 'admin'));
      const basicInfoValid =
         firstName &&
         lastName &&
         (iecRequired ? iec_no : true) &&
         email &&
         isPasswordValid;

      // For group_head adding an admin: no access dropdowns are shown, just require basic info
      if (userDetails.role === 'group_head') {
         setIsFormValid(!!basicInfoValid);
         return;
      }

      if (userType === 'group_head' && userDetails.role === 'superadmin') {
         if (
            basicInfoValid &&
            (selectedReportAccess && selectedReportAccess.length > 0) &&
            (selectedDocumentAccess.includes(0) || documentLinks.length > 0)
         ) {
            setIsFormValid(true);
         } else {
            setIsFormValid(false);
         }
         return;
      }

      if (
         basicInfoValid &&
         (userDetails.role !== 'superadmin' || (selectedReportAccess && selectedReportAccess.length > 0)) &&
         (selectedDocumentAccess.includes(0) || documentLinks.length > 0)
      ) {
         setIsFormValid(true);
      } else {
         setIsFormValid(false);
      }
   }, [formData, selectedReportAccess, userDetails.role, userDetails.group_id, passwordError, selectedDocumentAccess, documentLinks, selectedGroup, userType, enableSSO]);

   useEffect(() => {
      checkFormValid()
   }, [checkFormValid, formData])

   const errorFunction = (content) => {
      messageApi.open({
         type: 'error',
         content: content,
      });
   };

   const handleChange = (value) => {
      setSelectedReportAccess(value);
      checkFormValid();
   };

   const getAllGroups = async () => {
      try {
         const token = localStorage.getItem('token');
         const response = await getAllGroupsService(token);
         console.log('Fetched groups response:', response);
         if (response && response.data) {
            const options = response.data.map((group) => ({
               label: group.name,
               value: group.id,
               key: `group-${group.id}`
            }));
            setGroupOptions(options);
         }
      } catch (error) {
         console.error('Error fetching groups:', error);
      }
   };

   useEffect(() => {
      const fetchUserDetails = async () => {
         try {
            const token = localStorage.getItem('token');
            const response = await userAccount(token);
            if (response && response.data && response.data.data) {
               const userData = response.data.data;
               if (userData.group_id) {
                  localStorage.setItem('group_id', userData.group_id);
                  setUserDetails(prev => ({ ...prev, group_id: userData.group_id }));
               }
               if (userData.role === 'group_head' || userData.role === 'admin') {
                  setEnableSSO(true);
               } else if (userData.email && userData.email.includes('@')) {
                  const ssoRes = await checkSsoService(userData.email);
                  if (ssoRes && ssoRes.data && ssoRes.data.sso_enabled) {
                     setEnableSSO(true);
                  }
               }
            }
         } catch (error) {
            console.error('Error fetching user account details:', error);
         }
      };
      fetchUserDetails();
   }, [userDetails.role, userDetails.group_id]);

   useEffect(() => {
      getAllGroups();
      if (userDetails.role === 'group_head' && userDetails.group_id) {
         setSelectedGroup(userDetails.group_id);
      }
   }, [userDetails.role, userDetails.group_id]);

   const handleCipUploadChange = (value) => {
      setSelectedUploadAccess(value);
      checkFormValid();
   }

   const handleCipTemplateChange = (value) => {
      setSelectedTemplateAccess(value);
      checkFormValid();
   }

   const handleCipMasterUploadsChange = (value) => {
      setSelectedMasterUploadAccess(value);
      checkFormValid();
   }

   const handleCipLinkChange = (value) => {
      setSelectedDocumentAccess([value]);
      //if "No" is selected
      if (value !== 1) {
         setLinkType('');
         setLinkURL('');
      }
   };

   const handleCipGraphsChange = (value) => {
      setSelectedGraphsAccess([value]);
   };

   const handleRemoveDocumentLink = (type) => {
      setDocumentLinks(prev => prev.filter(link => link.type !== type));
   };

   const handleAddDocumentLink = () => {
      if (!linkType || !linkURL) return;

      setDocumentLinks(prevLinks => {
         // Check if the type already exists and replace existing link
         const existingIndex = prevLinks.findIndex(link => link.type === linkType);
         if (existingIndex !== -1) {
            const updatedLinks = [...prevLinks];
            updatedLinks[existingIndex] = { type: linkType, link: linkURL };
            return updatedLinks;
         }
         // Else, add new
         return [...prevLinks, { type: linkType, link: linkURL }];
      });

      setLinkType('');
      setLinkURL('');
   };

   const successFunction = (content) => {
      messageApi.open({
         type: 'success',
         content: content,
      });
   };

   const getCipPages = async () => {
      try {
         const token = localStorage.getItem('token');
         getCipPagesService(token).then(response => {

            if (response.data.data) {

               let selectOptions = [];
               selectOptions.push({
                  label: 'All Reports',
                  value: 0,
                  key: '0'
               })

               let selectUploadOptions = [];
               selectUploadOptions.push({
                  label: 'None',
                  value: 0,
                  key: '0'
               })

               let selectTemplateOptions = [];
               selectTemplateOptions.push({
                  label: 'None',
                  value: 0,
                  key: '0'
               })

               let selectMasterUploadOptions = [];
               selectMasterUploadOptions.push({
                  label: 'None',
                  value: 0,
                  key: '0'
               })
               let selectDocuemntOptions = [];
               let documentPageId = null;

               for (let i = 0; i < response.data.data.length; i++) {
                  const item = response.data.data[i];
                  if (item.page_type == 'REPORT') {
                     const obj = {
                        label: item.page_name,
                        value: item.page_id,
                        key: '0-' + i
                     }
                     selectOptions.push(obj)
                  }

                  if (item.page_type == 'UPLOAD') {
                     const obj = {
                        label: item.page_name,
                        value: item.page_id,
                        key: '0-' + i
                     }
                     selectUploadOptions.push(obj);
                  }

                  if (item.page_type == 'TEMPLATE') {
                     const obj = {
                        label: item.page_name,
                        value: item.page_id,
                        key: '0-' + i
                     }
                     selectTemplateOptions.push(obj);
                  }

                  if (item.page_type === 'DOCUMENT') {
                     documentPageId = item.page_id;
                     selectDocuemntOptions.push({
                        label: item.page_name,
                        value: item.page_id,
                        key: '0-' + i
                     });
                  }

                  if (item.page_type == 'MASTER-UPLOADS') {
                     const obj = {
                        label: item.page_name,
                        value: item.page_id,
                        key: '0-' + i
                     }
                     selectMasterUploadOptions.push(obj);
                  }

                  if (item.page_type === 'GRAPHS') {
                     setGraphsPageId(item.page_id);
                  }
               }
               setCipPages(selectOptions);
               setCipUploadPages(selectUploadOptions);
               setCipTemplatePages(selectTemplateOptions);
               setCipMasterUploadPages(selectMasterUploadOptions);
               setSelectedReportAccess([selectOptions[0].value])
               setSelectedUploadAccess([selectUploadOptions[0].value])
               setSelectedTemplateAccess([selectTemplateOptions[0].value])
               setSelectedMasterUploadAccess([selectMasterUploadOptions[0].value])
               setDocumentPageId(documentPageId);
            }
            else {
               setCipPages([])
            }


         }, error => {
            console.log("error while getting response = ", error)
         })
      }
      catch (error) {
         console.log("error")
      }
   }

   const handleInputChange = (e) => {
      const { id, value } = e.target;
      setFormData((prevData) => ({
         ...prevData,
         [id]: value,
      }));

      if (id === 'email' && value && value.includes('@') && value.split('@')[1].includes('.')) {
         checkSsoService(value).then(res => {
            if (res && res.data && res.data.sso_enabled) {
               setEnableSSO(true);
            }
         }).catch(() => {});
      }
   };

   const handleFromDateChange = (date, dateString) => {
      setFormData((prevData) => ({
         ...prevData,
         data_visible_from_dt: date ? dateString : null,
      }));
   };

   const handleToDateChange = (date, dateString) => {
      setFormData((prevData) => ({
         ...prevData,
         data_visible_to_dt: date ? dateString : null,
      }));
   };

   useEffect(() => {
      getCipPages();
   }, [userDetails.role]);

   const ensureGroupId = async () => selectedGroup || null;

   const handleSearchAdmin = async () => {
      if (!formData.iec_no) {
         messageApi.error("Please enter an IEC number first");
         return;
      }
      setSearchingAdmin(true);
      setExistingAdmin(null);
      try {
         const token = localStorage.getItem('token');
         const response = await searchAdminByIecService(token, formData.iec_no);
         if (response && response.status === 200) {
            setExistingAdmin(response.data);
            messageApi.success("Admin found!");
         } else {
            messageApi.info(response.msg || "No existing admin found with this IEC");
         }
      } catch (error) {
         console.error("Error searching admin:", error);
         messageApi.error("Error searching for admin");
      } finally {
         setSearchingAdmin(false);
      }
   };

   const handleClaimAdmin = async () => {
      if (!existingAdmin || !userDetails.group_id) return;

      try {
         const token = localStorage.getItem('token');
         const response = await claimAdminToGroupService(token, {
            iec_no: existingAdmin.iec_no,
            group_id: userDetails.group_id
         });

         if (response && response.status === 200) {
            messageApi.success(response.msg || "Admin added to your group successfully");
            // Redirect or Refresh
            setTimeout(() => {
               window.location.href = '/dashboard/app/user-list';
            }, 1500);
         } else {
            messageApi.error(response.msg || "Failed to add admin to group");
         }
      } catch (error) {
         console.error("Error claiming admin:", error);
         messageApi.error("Error adding admin to group");
      }
   };

   const handleAddUser = async () => {
      try {

         if (formData.data_visible_from_dt && formData.data_visible_to_dt) {
            const fromDate = dayjs(formData.data_visible_from_dt);
            const toDate = dayjs(formData.data_visible_to_dt);
            if (toDate.isBefore(fromDate)) {
               errorFunction('To-Date cannot be before From-Date');
               return;
            }
         }

         if (userDetails.role == 'superadmin' && userType === 'admin' && (selectedReportAccess.length == 0 || selectedUploadAccess.length == 0 || selectedTemplateAccess.length == 0)) {
            throw new Error("Error submitting form, access not selected")
         }
         if (userDetails.role == 'superadmin' && userType === 'admin' && formData.iec_no.length == 0) {
            throw new Error("Error submitting form no IEC number entered");
         }

         let finalPageIds = [];
         if (Array.isArray(selectedReportAccess)) {
            if (selectedReportAccess.includes(0)) {
               finalPageIds = reportAccessOptions.map(o => o.value).filter(o => o !== 0)
            } else {
               selectedReportAccess.forEach(i => {
                  if (i !== 0) {
                     finalPageIds.push(i)
                  }
               })
            }
         }

         let finalUploadPageIds = [];

         if (Array.isArray(selectedUploadAccess)) {
            selectedUploadAccess.forEach(i => {
               if (i !== 0) {
                  finalUploadPageIds.push(i)
               }
            })
         }

         let finalTemplatePageIds = [];

         if (Array.isArray(selectedTemplateAccess)) {
            selectedTemplateAccess.forEach(i => {
               if (i !== 0) {
                  finalTemplatePageIds.push(i)
               }
            })
         }

         let finalMasterUploadPageIds = [];

         if (Array.isArray(selectedMasterUploadAccess)) {
            selectedMasterUploadAccess.forEach(i => {
               if (i !== 0) {
                  finalMasterUploadPageIds.push(i)
               }
            })
         }

         let mergedPageIds = [...new Set([...finalPageIds, ...finalUploadPageIds, ...finalTemplatePageIds, ...finalMasterUploadPageIds])];
         if (Array.isArray(selectedDocumentAccess) && selectedDocumentAccess.includes(1) && documentPageId) {
            mergedPageIds.push(documentPageId);
         }
         if (Array.isArray(selectedGraphsAccess) && selectedGraphsAccess.includes(1) && graphsPageId) {
            mergedPageIds.push(graphsPageId);
         }

         const group_id = await ensureGroupId();

         // SSO users have no local password — the IdP owns it. Send auth_type='sso'
         // and no password; the backend stores password=NULL. Local users send their
         // (optionally auto-generated) password as before.
         const authType = enableSSO ? 'sso' : 'local';
         const userPassword = enableSSO ? null : formData.password;

         const create_data = {
            fullname: formData.firstName + ' ' + formData.lastName,
            email: formData.email,
            iec_no: formData.iec_no,
            mobile: formData.mobile,
            password: userPassword,
            auth_type: authType,
            page_ids: mergedPageIds,
            cc: formData.cc,
            email_to: formData.alternateEmail,
            bcc: formData.bcc,
            multi_factor_check: formData.multi_factor_check,
            document_links: documentLinks,
            data_visible_from_dt: formData.data_visible_from_dt,
            data_visible_to_dt: formData.data_visible_to_dt,
            br: groupOptions.find(opt => opt.value === group_id)?.label || '',
            group_id: group_id,
            group_head: formData.group_head ? 1 : 0,
            role: formData.group_head ? 'group_head' : 'admin'
         };
         setUserData(create_data);
         const response = await AddAdminUserService(create_data);
         const data = await response;
         if (data) {
            if (data.status != 200) {
               setIsUserCreated(false);
               errorFunction(data.msg);
            }
            else {
               setIsUserCreated(true);
               successFunction(data.msg);
               
               if (enableSSO && ssoData.domain_name) {
                  try {
                     const token = localStorage.getItem('token');
                     const provider = (ssoData.tenant_id && ssoData.tenant_id.includes('auth0.com')) ? 'auth0' : ssoData.sso_provider;
                     await createSsoConfigService(token, {
                        domain_name: ssoData.domain_name,
                        sso_provider: provider,
                        tenant_id: ssoData.tenant_id,
                        client_id: ssoData.client_id,
                        client_secret: ssoData.client_secret,
                        is_active: ssoData.is_active
                     });
                     messageApi.success("SSO Configuration saved successfully!");
                  } catch (ssoError) {
                     console.error("Error saving SSO Config:", ssoError);
                     messageApi.error("User created, but failed to save SSO config.");
                  }
               }
            }

         }
      } catch (error) {
         console.error('Error adding new user:', error);
         errorFunction(error?.message);
      }
   };

   const validateEmails = (emailList = []) => {
      const uniqueEmails = []
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      for (let i of emailList) {
         if (!uniqueEmails.includes(i) && emailRegex.test(i)) {
            uniqueEmails.push(i)
         }
      }
      return uniqueEmails
   }

   const handleAddGroup = async () => {
      try {
         const groupName = newGroupName.trim();
         if (!groupName) {
            message.error('Please enter group name');
            return;
         }

         const token = localStorage.getItem('token');
         const response = await createGroupService(token, groupName);
         if (response && response.status === 201) {
            message.success('Group added successfully');

            // Manually add the new group to options to avoid the race condition where 
            // the Select shows the ID instead of the name because getAllGroups hasn't finished.
            if (response.data && response.data.id) {
               const newOption = {
                  label: response.data.name,
                  value: response.data.id,
                  key: `group-${response.data.id}`
               };
               setGroupOptions(prev => [...prev, newOption]);
               setSelectedGroup(response.data.id);
            }

            await getAllGroups(); // Still refresh to ensure everything is in sync
            setNewGroupName('');
            setShowAddGroupModal(false);
            setSearchText('');
         } else {
            message.error(response.msg || 'Error adding group');
         }
      } catch (error) {
         console.error('Error adding group:', error);
         message.error('Error adding group');
      }
   };

   const openAddGroupModal = (e) => {
      if (e) {
         e.preventDefault();
         e.stopPropagation();
      }
      setNewGroupName(searchText.trim());
      setShowAddGroupModal(true);
   };

   const validatePassword = (password) => {
      const rules = [
         /.{11,}/, // At least 11 characters
         /[a-z]/, // Lowercase
         /[A-Z]/, // Uppercase
         /\d/, // Number
         /[!@#$%^&*()_\-+={}[\]|:;"'<>,.?/]/ // Special character
      ];
      // finds the no of rules password has passed (out of 5) and calculates strength % out of 100
      let passed = rules.filter((rule) => rule.test(password)).length;
      setStrength((passed / rules.length) * 100);

      if (passed === rules.length) {
         setPasswordError("");
      } else {
         setPasswordError("Password must be at least 11 characters long and include an uppercase, a lowercase, a number, and a special character.");
      }
   };

   const generatePassword = () => {
      const uppercase = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
      const lowercase = "abcdefghijklmnopqrstuvwxyz";
      const numbers = "0123456789";
      const symbols = "!@#$%^&*()_-+={}[]|:;\"'<>,.?/";

      let password = "";
      // make sure it contains one of each (uppercase, lowercase, number, symbols)
      password += uppercase[Math.floor(Math.random() * uppercase.length)];
      password += lowercase[Math.floor(Math.random() * lowercase.length)];
      password += numbers[Math.floor(Math.random() * numbers.length)];
      password += symbols[Math.floor(Math.random() * symbols.length)];

      // Fill the rest randomly to make it at least 11 characters
      while (password.length < 11) {
         const allChars = uppercase + lowercase + numbers + symbols;
         password += allChars[Math.floor(Math.random() * allChars.length)];
      }
      // Shuffle the password to avoid predictable pattern
      password = password.split('').sort(() => 0.5 - Math.random()).join('');

      setFormData((prev) => ({ ...prev, password }));
      validatePassword(password);
   };

   const renderMailList = (emailList = []) => {
      if (emailList.length === 0) {
         return null
      }
      if (emailList.length > 1) {
         return emailList.join(',')
      }
      return emailList[0]
   }


   return (
      <Fragment>
         {contextHolder}
         <Row>
            <Col xl="2" lg="4" className=""></Col>
            {isUserCreated ? (
               <Col xl="8" lg="8" className="">
                  <Card>
                     <Card.Header className="d-flex justify-content-between">
                        <div className="header-title">
                           <h4 className="card-title">User Created Successfully</h4>
                        </div>
                     </Card.Header>
                     <Card.Body>
                        <div className="mt-2">
                           <h6 className="mb-1">Name:</h6>
                           <p>{addUserData && addUserData.fullname}</p>
                        </div>
                        <div className="mt-2">
                           <h6 className="mb-1">Email:</h6>
                           <p>
                              <Link to="#" className="text-body">
                                 {addUserData && addUserData.email}
                              </Link>
                           </p>
                        </div>
                        {!isEmpty(addUserData?.email_to) ?
                           <div className="mt-2">
                              <h6 className="mb-1">Alternate Emails:</h6>
                              <p>
                                 <Link to="#" className="text-body">
                                    {addUserData && renderMailList(addUserData.email_to)}
                                 </Link>
                              </p>
                           </div> : null}
                        {!isEmpty(addUserData?.cc) ?
                           <div className="mt-2">
                              <h6 className="mb-1">CC:</h6>
                              <p>
                                 <Link to="#" className="text-body">
                                    {addUserData && renderMailList(addUserData.cc)}
                                 </Link>
                              </p>
                           </div> : null}
                        {!isEmpty(addUserData?.bcc) ?
                           <div className="mt-2">
                              <h6 className="mb-1">BCC:</h6>
                              <p>
                                 <Link to="#" className="text-body">
                                    {addUserData && renderMailList(addUserData.bcc)}
                                 </Link>
                              </p>
                           </div> : null}
                        <div className="mt-2">
                           <h6 className="mb-1">Mobile:</h6>
                           <p>
                              <Link to="#" className="text-body">
                                 {addUserData && addUserData.mobile}
                              </Link>
                           </p>
                        </div>
                        <div className="mt-2">
                           <h6 className="mb-1">IEC:</h6>
                           <p>
                              <Link to="#" className="text-body">
                                 {addUserData && addUserData.iec_no}
                              </Link>
                           </p>
                        </div>
                        <div className="mt-2">
                           <h6 className="mb-1">{addUserData?.auth_type === 'sso' ? 'Authentication:' : 'Password:'}</h6>
                           <p>
                              <Link to="#" className="text-body">
                                 {addUserData?.auth_type === 'sso'
                                    ? 'Managed via SSO — no local password'
                                    : addUserData && addUserData.password}
                              </Link>
                           </p>
                        </div>
                        <div className="mt-2">
                           <h6 style={{ float: 'right' }} className="mb-1">
                              <Link onClick={() => setIsUserCreated(false)} to="#" className="text-body">
                                 Go Back
                              </Link>
                           </h6>
                        </div>
                     </Card.Body>
                  </Card>
               </Col>
            ) : (
               <Col xl="8" lg="8">
                  <Card>
                     <Card.Header className="d-flex justify-content-between">
                        <div className="header-title">
                           <h4 className="card-title">New User Information</h4>
                        </div>
                     </Card.Header>
                     <Card.Body>
                        <div className="new-user-info">
                           <Form>
                              <div className="row">
                                 {userDetails.role === 'superadmin' && (
                                    <Form.Group className="col-md-12 form-group border-bottom pb-3 mb-3">
                                       <Form.Label>Add For: </Form.Label>
                                       <div className="d-flex align-items-center gap-4">
                                          <Form.Check
                                             inline
                                             type="radio"
                                             name="addFor"
                                             id="addForAdmin"
                                             label="Admin"
                                             checked={userType === 'admin'}
                                             onChange={() => {
                                                const val = 'admin';
                                                setUserType(val);
                                                setFormData((prev) => ({
                                                   ...prev,
                                                   group_head: 0,
                                                   role: 'admin'
                                                }));
                                             }}
                                          />
                                          <Form.Check
                                             inline
                                             type="radio"
                                             name="addFor"
                                             id="addForGroupHead"
                                             label="Group Head"
                                             checked={userType === 'group_head'}
                                             onChange={() => {
                                                const val = 'group_head';
                                                setUserType(val);
                                                setFormData((prev) => ({
                                                   ...prev,
                                                   group_head: 1,
                                                   role: 'group_head'
                                                }));
                                             }}
                                          />
                                       </div>
                                    </Form.Group>
                                 )}
                                 <Form.Group className="col-md-12 form-group">
                                    <Form.Label htmlFor="firstName">Client Name:</Form.Label>
                                    <Form.Control type="text" name="firstName" id="firstName" placeholder="Client Name" onChange={handleInputChange} />
                                 </Form.Group>
                                 <Form.Group className="col-md-6 form-group">
                                    <Form.Label htmlFor="lastName">Alias:</Form.Label>
                                    <Form.Control type="text" id="lastName" placeholder="Alias" onChange={handleInputChange} />
                                 </Form.Group>
                                 {userType === 'admin' && (
                                    (userDetails.role === 'superadmin' || userDetails.role === 'group_head') ? (
                                       <Form.Group className="col-md-6 form-group">
                                          <Form.Label htmlFor="iec_no">IEC:</Form.Label>
                                          <div className="d-flex"><Form.Control type="text" id="iec_no" placeholder="IEC" onChange={handleInputChange} />{userDetails.role === "group_head" && (<Button variant="primary" className="ms-2" onClick={handleSearchAdmin} disabled={searchingAdmin}>{searchingAdmin ? "..." : "Search"}</Button>)}</div>
                                       </Form.Group>
                                    ) : (
                                       <Form.Group className="col-md-6 form-group">
                                          <Form.Label htmlFor="iec_no">IEC:</Form.Label>
                                          <Form.Control type="text" id="iec_no" value={userDetails.iec} placeholder="IEC" disabled />
                                       </Form.Group>
                                    )
                                 )}
                                 {userType === 'group_head' && userDetails.role === 'superadmin' && (
                                    <Form.Group className="col-md-6 form-group">
                                       <Form.Label htmlFor="iec_no">IEC: <span className="text-muted" style={{ fontSize: '0.85em' }}>(Optional)</span></Form.Label>
                                       <Form.Control type="text" id="iec_no" placeholder="IEC (optional)" onChange={handleInputChange} />
                                    </Form.Group>
                                 )}
                                 {userType === 'group_head' && userDetails.role !== 'superadmin' && (
                                    <Form.Group className="col-md-6 form-group">
                                       <Form.Label htmlFor="iec_no">IEC:</Form.Label>
                                       <Form.Control type="text" id="iec_no" value={userDetails.iec} placeholder="IEC" disabled />
                                    </Form.Group>
                                 )}
                                 {existingAdmin && (
                                    <Col md="12" className="mb-3">
                                       <div className="alert alert-info d-flex justify-content-between align-items-center">
                                          <div>
                                             <strong>Existing Admin Found:</strong> {existingAdmin.fullname} ({existingAdmin.email})
                                             {existingAdmin.group_id && <div className="text-danger mt-1">Note: This admin already belongs to a group.</div>}
                                          </div>
                                          {!existingAdmin.group_id && (
                                             <Button variant="success" size="sm" onClick={handleClaimAdmin}>
                                                Add to My Group
                                             </Button>
                                          )}
                                       </div>
                                    </Col>
                                 )}
                                 <Form.Group className="col-md-6 form-group">
                                    <Form.Label htmlFor="mobile">Mobile Number:</Form.Label>
                                    <Form.Control type="text" id="mobile" placeholder="Mobile Number" onChange={handleInputChange} />
                                 </Form.Group>
                                 <Form.Group className="col-md-6 form-group">
                                    <Form.Label htmlFor="email">User Email:</Form.Label>
                                    <Form.Control type="email" id="email" placeholder="Email" onChange={handleInputChange} />
                                 </Form.Group>
                                 {userDetails.role === 'superadmin' && (userType === 'admin' || userType === 'group_head') && (
                                    <Form.Group className="col-md-6 form-group">
                                       <Form.Label htmlFor="email">Select Report Access:</Form.Label>
                                       <Select
                                          mode="multiple"
                                          allowClear
                                          style={{
                                             width: '100%',
                                          }}
                                          placeholder="Please select"
                                          defaultValue={reportAccessOptions[0]}
                                          onChange={handleChange}
                                          maxTagCount='responsive'
                                          options={reportAccessOptions}
                                          value={selectedReportAccess}
                                       />
                                    </Form.Group>
                                 )}
                                 {userDetails.role === 'superadmin' && (userType === 'admin' || userType === 'group_head') && (
                                    <Form.Group className="col-md-6 form-group">
                                       <Form.Label htmlFor="email">Select Upload Access:</Form.Label>
                                       <Select
                                          mode="multiple"
                                          allowClear
                                          style={{
                                             width: '100%',
                                          }}
                                          placeholder="Please select uploads"
                                          defaultValue={uploadAccessOptions[0]}
                                          onChange={handleCipUploadChange}
                                          maxTagCount='responsive'
                                          options={uploadAccessOptions}
                                          value={selectedUploadAccess}
                                       />
                                    </Form.Group>
                                 )}
                                 {userDetails.role === 'superadmin' && (userType === 'admin' || userType === 'group_head') && (
                                    <Form.Group className="col-md-6 form-group">
                                       <Form.Label htmlFor="email">Select Template Access:</Form.Label>
                                       <Select
                                          mode="multiple"
                                          allowClear
                                          style={{
                                             width: '100%',
                                          }}
                                          placeholder="Please select report templates"
                                          defaultValue={templateAccessOptions[0]}
                                          onChange={handleCipTemplateChange}
                                          maxTagCount='responsive'
                                          options={templateAccessOptions}
                                          value={selectedTemplateAccess}
                                       />
                                    </Form.Group>
                                 )}
                                 {userDetails.role === 'superadmin' && (userType === 'admin' || userType === 'group_head') && (
                                    <Form.Group className="col-md-6 form-group">
                                       <Form.Label>Select Document Access:</Form.Label>
                                       <Select
                                          style={{ width: '100%' }}
                                          value={selectedDocumentAccess}
                                          onChange={handleCipLinkChange}
                                          options={DocumentAccessOptions}
                                       />
                                    </Form.Group>
                                 )}
                                 {selectedDocumentAccess.includes(1) && (
                                    <>
                                       <Form.Group className="col-md-6 form-group">
                                          <div className="d-flex gap-2">
                                             <div style={{ flex: '1' }}>
                                                <Form.Label style={{ fontSize: '0.9rem' }}>Link Type:</Form.Label>
                                                <Select
                                                   style={{ width: '100%' }}
                                                   placeholder="Select type"
                                                   value={linkType}
                                                   onChange={setLinkType}
                                                   options={linkTypeOptions}
                                                   allowClear
                                                />
                                             </div>
                                             <div style={{ flex: '1' }}>
                                                <Form.Label style={{ fontSize: '0.9rem' }}>Enter Link:</Form.Label>
                                                <Form.Control
                                                   type="text"
                                                   placeholder="Paste link"
                                                   value={linkURL}
                                                   onChange={(e) => setLinkURL(e.target.value)}
                                                />
                                             </div>
                                             <div style={{ flex: '0 0 auto' }} className="d-flex align-items-end">
                                                <Button
                                                   variant="primary"
                                                   type="button"
                                                   style={{ whiteSpace: 'nowrap', height: '36px' }}
                                                   onClick={handleAddDocumentLink}
                                                >
                                                   Add
                                                </Button>
                                             </div>
                                          </div>
                                       </Form.Group>
                                       {/* displays the added links as buttons below */}
                                       <div className="d-flex flex-wrap gap-2">
                                          {documentLinks.map((item, index) => (
                                             <Button
                                                key={index}
                                                variant="outline-secondary"
                                                size="sm"
                                                onClick={() => window.open(item.link, '_blank')}
                                             >
                                                {item.type}
                                                <span style={{ marginLeft: '4px', color: 'red' }}
                                                   onClick={(e) => {
                                                      e.stopPropagation();
                                                      handleRemoveDocumentLink(item.type);
                                                   }}
                                                > ✕ </span>
                                             </Button>
                                          ))}
                                       </div>
                                    </>
                                 )}
                                 {userDetails.role === 'superadmin' && (userType === 'admin' || userType === 'group_head') && (
                                    <Form.Group className="col-md-6 form-group">
                                       <Form.Label htmlFor="email">Select Master-Upload Access:</Form.Label>
                                       <Select
                                          mode="multiple"
                                          allowClear
                                          style={{
                                             width: '100%',
                                          }}
                                          placeholder="Please select master uploads"
                                          defaultValue={masterUploadAccessOptions[0]}
                                          onChange={handleCipMasterUploadsChange}
                                          maxTagCount='responsive'
                                          options={masterUploadAccessOptions}
                                          value={selectedMasterUploadAccess}
                                       />
                                    </Form.Group>
                                 )}
                                 {userDetails.role === 'superadmin' && (userType === 'admin' || userType === 'group_head') && (
                                    <Form.Group className="col-md-6 form-group">
                                       <Form.Label>Select Graphs Access:</Form.Label>
                                       <Select
                                          style={{ width: '100%' }}
                                          value={selectedGraphsAccess}
                                          onChange={handleCipGraphsChange}
                                          options={GraphsAccessOptions}
                                       />
                                    </Form.Group>
                                 )}
                                 {userDetails.role === 'superadmin' && (userType === 'admin' || userType === 'group_head') && (
                                    <>
                                       <Form.Group className="col-md-3 form-group">
                                          <Form.Label>Data Access From:</Form.Label>
                                          <DatePicker
                                             style={{ width: '100%' }}
                                             placeholder="Start Date"
                                             onChange={handleFromDateChange}
                                          />
                                       </Form.Group>
                                       <Form.Group className="col-md-3 form-group">
                                          <Form.Label>Data Access To:</Form.Label>
                                          <DatePicker
                                             style={{ width: '100%' }}
                                             placeholder="End Date"
                                             onChange={handleToDateChange}
                                             allowClear
                                          />
                                       </Form.Group>
                                    </>
                                 )}
                                 {userDetails.role === 'superadmin' && (
                                    <Form.Group className="col-md-8 form-group">
                                       <Form.Label>Select Group:</Form.Label>
                                       <div className="d-flex align-items-end gap-2">
                                          <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                                             <Select
                                                className="w-100"
                                                style={{ width: '100%' }}
                                                onChange={(val) => {
                                                   setSelectedGroup(val);
                                                   setSearchText('');
                                                }}
                                                showSearch
                                                searchValue={searchText}
                                                onSearch={(val) => setSearchText(val)}
                                                optionFilterProp="label"
                                                filterOption={(input, option) =>
                                                   (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                                }
                                                notFoundContent={
                                                   searchText.trim() ? (
                                                      <div className="text-danger" style={{ fontSize: '0.85rem' }}>
                                                         This group doesn&apos;t exist.
                                                      </div>
                                                   ) : (
                                                      'No groups found'
                                                   )
                                                }
                                                maxTagCount="responsive"
                                                options={groupOptions}
                                                placeholder="Select or type a group"
                                                value={selectedGroup}
                                                allowClear
                                             />
                                          </div>
                                          {isGroupNotFound && (
                                             <div style={{ flex: '0 0 auto' }}>
                                                <Button
                                                   type="button"
                                                   variant="primary"
                                                   onMouseDown={openAddGroupModal}
                                                   style={{ whiteSpace: 'nowrap', minWidth: '120px', height: '38px' }}
                                                >
                                                   Add Group
                                                </Button>
                                             </div>
                                          )}
                                       </div>
                                    </Form.Group>
                                 )}
                                 <Form.Group className="col-md-12 form-group mt-2">
                                    {userType == "admin" && (<Form.Check className="form-check">
                                       <Form.Check.Input
                                          type="checkbox"
                                          id="group_head"
                                          checked={!!formData.group_head}
                                          onChange={(e) => {
                                             setFormData((prev) => ({
                                                ...prev,
                                                group_head: e.target.checked ? 1 : 0
                                             }));
                                          }}
                                       />
                                       <Form.Check.Label htmlFor="group_head" className="ms-2">
                                          Make this user Group Head
                                       </Form.Check.Label>
                                    </Form.Check>)}
                                 </Form.Group>

                                 <h5 className="mb-3 mt-3">Alternate Emails</h5>
                                 <div className="row">
                                    {userDetails.role === 'superadmin' && (
                                       <Form.Group className="col-md-6 form-group">
                                          <Form.Label htmlFor="alternate-email">To</Form.Label>
                                          <Select
                                             mode="tags"
                                             allowClear
                                             style={{
                                                width: '100%',
                                             }}
                                             dropdownStyle={{ display: 'none' }}
                                             placeholder="Please alternate enter email"
                                             defaultValue={[]}
                                             value={formData.alternateEmail}
                                             onChange={(e) => {
                                                setFormData((prevData) => ({
                                                   ...prevData,
                                                   'alternateEmail': validateEmails(e),
                                                }));
                                             }}
                                             maxTagCount='responsive'
                                             options={[]}
                                          />
                                       </Form.Group>
                                    )
                                    }
                                    {userDetails.role === 'superadmin' && (
                                       <Form.Group className="col-md-6 form-group">
                                          <Form.Label htmlFor="email-cc">Cc</Form.Label>
                                          <Select
                                             mode="tags"
                                             allowClear
                                             dropdownStyle={{ display: 'none' }}
                                             style={{
                                                width: '100%',
                                             }}
                                             placeholder="Please enter email"
                                             defaultValue={[]}
                                             value={formData.cc}
                                             onChange={(e) => {
                                                setFormData((prevData) => ({
                                                   ...prevData,
                                                   'cc': validateEmails(e),
                                                }));

                                             }}
                                             maxTagCount='responsive'
                                             options={[]}
                                          />
                                       </Form.Group>
                                    )
                                    }
                                    {userDetails.role === 'superadmin' && (
                                       <Form.Group className="col-md-6 form-group">
                                          <Form.Label htmlFor="email-bcc">Bcc</Form.Label>
                                          <Select
                                             mode="tags"
                                             allowClear
                                             dropdownStyle={{ display: 'none' }}
                                             style={{
                                                width: '100%',
                                             }}
                                             placeholder="Please enter email"
                                             defaultValue={[]}
                                             value={formData.bcc}
                                             onChange={(e) => {
                                                setFormData((prevData) => ({
                                                   ...prevData,
                                                   'bcc': validateEmails(e),
                                                }));
                                             }}
                                             maxTagCount='responsive'
                                             options={[]}
                                          />
                                       </Form.Group>
                                    )
                                    }
                                 </div>

                              </div>
                              <hr />
                              <div className="d-flex justify-content-between align-items-center mb-3">
                                 <h5 className="mb-0">Security</h5>
                                 {/* For superadmin->admin the dedicated SSO section below owns this toggle
                                     (it also collects IdP config). For every other creator, expose a clear
                                     control here so the password field never appears/disappears silently. */}
                                 {!(userDetails.role === 'superadmin' && userType === 'admin') && (
                                    <Form.Check
                                       type="switch"
                                       id="security_enable_sso"
                                       label="Signs in via SSO (no local password)"
                                       checked={enableSSO}
                                       onChange={(e) => setEnableSSO(e.target.checked)}
                                    />
                                 )}
                              </div>
                              <div className="row">
                                 {enableSSO ? (
                                    <Form.Group className="col-md-12 form-group">
                                       <div className="alert alert-info py-2 mb-0" style={{ borderRadius: '8px' }}>
                                          🔑 <strong>SSO Enabled:</strong> Password is managed directly by your SSO Identity Provider (Auth0 / Azure AD). Local password creation is not required.
                                       </div>
                                    </Form.Group>
                                 ) : (
                                    <Form.Group className="col-md-6 form-group">
                                       <Form.Label htmlFor="password">Password <span className="text-muted" style={{ fontSize: '0.85em' }}>(local login credential)</span>:</Form.Label>
                                       <div style={{ display: 'flex', gap: '5px' }}>
                                          <Input.Password
                                             type="password"
                                             id="password"
                                             placeholder="Password"
                                             allowClear
                                             value={formData.password}
                                             onChange={(e) => {
                                                const { id, value } = e.target;
                                                setFormData((prev) => ({
                                                   ...prev,
                                                   [id]: value,
                                                }));
                                                validatePassword(value);
                                             }}
                                             iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
                                             status={passwordError ? "error" : ""} //red border for error
                                          />
                                          <Button variant="btn btn-primary" style={{ fontSize: '0.85rem', padding: '2px 8px', whiteSpace: 'nowrap' }}
                                             type="button"
                                             onClick={generatePassword}>
                                             Auto-generate
                                          </Button>
                                       </div>
                                       <div className="mt-2">
                                          <Progress percent={strength} showInfo={false}
                                             strokeColor={strength < 60 ? 'red' : strength < 80 ? 'orange' : 'green'} />
                                       </div>
                                       {passwordError && (
                                          <div className="text-danger mt-1">
                                             {passwordError}
                                          </div>
                                       )}
                                    </Form.Group>
                                 )}
                                 {userDetails.role === 'superadmin' && !enableSSO && (
                                    <Form.Group className="col-md-6 form-group">
                                       <Form.Check className="form-check">
                                          <Form.Check.Input type="checkbox" id="multi_factor_check" checked={formData.multi_factor_check}
                                             onChange={(e) => {
                                                const { id, checked } = e.target;
                                                setFormData((prev) => ({
                                                   ...prev,
                                                   [id]: checked,
                                                }));
                                             }}
                                          />
                                          <Form.Check.Label htmlFor="multi_factor_check" className="ms-2">Multi-Factor Authentication</Form.Check.Label>
                                       </Form.Check>
                                    </Form.Group>
                                 )}
                              </div>

                              {userDetails.role === 'superadmin' && userType === 'admin' && (
                                 <Fragment>
                                     <hr />
                                     <div className="d-flex justify-content-between align-items-center mb-3">
                                         <h5 className="mb-0">Single Sign-On (SSO)</h5>
                                         <Form.Check type="switch" id="enable_sso" label="Enable SSO" checked={enableSSO}
                                             onChange={(e) => {
                                                setEnableSSO(e.target.checked);
                                                if (e.target.checked && formData.email) {
                                                   const domain = formData.email.split('@')[1];
                                                   if (domain) setSsoData(prev => ({...prev, domain_name: domain}));
                                                }
                                             }}
                                         />
                                     </div>
                                     
                                     {enableSSO && (
                                         <div className="row">
                                             <Form.Group className="col-md-6 form-group">
                                                <Form.Label htmlFor="domain_name">Domain Name (e.g. company.com): <span className="text-danger">*</span></Form.Label>
                                                <Form.Control type="text" id="domain_name" value={ssoData.domain_name}
                                                    onChange={(e) => setSsoData({...ssoData, domain_name: e.target.value})}
                                                    placeholder="company.com" required
                                                />
                                             </Form.Group>
                                             <Form.Group className="col-md-6 form-group">
                                                <Form.Label htmlFor="tenant_id">Tenant ID (Azure AD): <span className="text-danger">*</span></Form.Label>
                                                <Form.Control type="text" id="tenant_id" value={ssoData.tenant_id}
                                                    onChange={(e) => setSsoData({...ssoData, tenant_id: e.target.value})}
                                                    placeholder="00000000-0000-0000-0000-000000000000" required
                                                />
                                             </Form.Group>
                                             <Form.Group className="col-md-6 form-group">
                                                <Form.Label htmlFor="client_id">Client ID: <span className="text-danger">*</span></Form.Label>
                                                <Form.Control type="text" id="client_id" value={ssoData.client_id}
                                                    onChange={(e) => setSsoData({...ssoData, client_id: e.target.value})}
                                                    placeholder="Client ID from Azure AD" required
                                                />
                                             </Form.Group>
                                             <Form.Group className="col-md-6 form-group">
                                                <Form.Label htmlFor="client_secret">Client Secret: <span className="text-danger">*</span></Form.Label>
                                                <Form.Control type="password" id="client_secret" value={ssoData.client_secret}
                                                    onChange={(e) => setSsoData({...ssoData, client_secret: e.target.value})}
                                                    placeholder="Secret Key" required
                                                />
                                             </Form.Group>
                                         </div>
                                     )}
                                 </Fragment>
                               )}

                              <Button style={{ float: "center" }} type="button" variant="btn btn-primary" onClick={handleAddUser} disabled={!isFormValid}>
                                 {(userDetails.role === 'superadmin' || userDetails.role === 'group_head') ? 'Add New Admin' : 'Add New User'}
                              </Button>
                           </Form>
                        </div>
                     </Card.Body>
                  </Card>
               </Col>
            )}
         </Row>
         <Modal show={showAddGroupModal} onHide={() => setShowAddGroupModal(false)} centered>
            <Modal.Header closeButton>
               <Modal.Title>Add Group</Modal.Title>
            </Modal.Header>
            <Modal.Body>
               <Form.Group>
                  <Form.Label>Group Name</Form.Label>
                  <Form.Control
                     type="text"
                     placeholder="Enter group name"
                     value={newGroupName}
                     onChange={(e) => setNewGroupName(e.target.value)}
                  />
               </Form.Group>
            </Modal.Body>
            <Modal.Footer>
               <Button variant="secondary" onClick={() => setShowAddGroupModal(false)}>
                  Cancel
               </Button>
               <Button variant="primary" onClick={handleAddGroup}>
                  Add Group
               </Button>
            </Modal.Footer>
         </Modal>
      </Fragment>
   );
});

AdminAdd.displayName = 'AdminAdd';
export default AdminAdd;
